import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewChild, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, RouterOutlet} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {SvgIconComponent} from 'angular-svg-icon';

import {TabsComponent} from '@components/ui/tabs/tabs.component';
import {ParentTab} from '@components/ui/tabs/tab';
import {TabList} from '@components/ui/tabs/tab-list';
import {PARAM_ENTITY_ID} from '../config/app-urls';
import {AppParameterMap} from '../config/app-parameter-map';
import {Authorization} from '@shared/models/authorization.model';
import {UserSessionService} from '@shared/services/user-session.service';
import {ClientDetailsService} from './client-details/client-details.service';
import {ClientDetailsConverterService} from './client-details/view-models/client-details-converter.service';
import {ClientDetailsViewModel} from './client-details/view-models/client-details-view.model';
import {EntityTabs} from './entity-tabs';
import {ShareClassService} from './share-register/share-classes/share-class.service';

@Component({
    selector: 'app-view-client',
    templateUrl: './view-client.component.html',
    styleUrls: ['./view-client.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TabsComponent, RouterOutlet, SvgIconComponent]
})
export class ViewClientComponent implements OnInit {

  clientCode: string;
  readonly clientDetailsVM = signal<ClientDetailsViewModel | null>(null);

  @ViewChild('tabsComponent', {static: true}) tabsComponent: TabsComponent;

  private readonly _route = inject(ActivatedRoute);
  private readonly _clientDetailsService = inject(ClientDetailsService);
  private readonly _clientDetailsConverter = inject(ClientDetailsConverterService);
  private readonly _shareClassService = inject(ShareClassService);
  private readonly _userSessionService = inject(UserSessionService);
  private readonly _titleService = inject(Title);
  private readonly _destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this._titleService.setTitle('Portal - Entities');
    this.clientCode = this._route.snapshot.paramMap.get(PARAM_ENTITY_ID.name);
    const authorizations = this._userSessionService.userSession.authorizations;
    if (authorizations.shares) {
      this._shareClassService.getShareClassesForClient(this.clientCode).pipe(
        takeUntilDestroyed(this._destroyRef)
      ).subscribe({
          next: (shareClasses) => {
            this.tabsComponent.setTabList(this.getTabsAuthorizedForUser(authorizations, shareClasses.length));
          },
          error: () => {
            this.tabsComponent.setTabList(this.getTabsAuthorizedForUser(authorizations, 0));
          }
        });
    } else {
      this.tabsComponent.setTabList(this.getTabsAuthorizedForUser(authorizations, 0));
    }
    this.getClientDetails(this.clientCode);
  }

  getClientDetails(clientCode: string) {
    this._clientDetailsService.getClientDetails(clientCode).pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(clientDetailsDTO => {
      this.clientDetailsVM.set(this._clientDetailsConverter.convert(clientDetailsDTO));
    });
  }

  private getTabsAuthorizedForUser(authorizations: Authorization, shareClassesLength: number): TabList {
    const tabs: ParentTab[] = [];

    if (authorizations.information) {
      tabs.push(EntityTabs.Information);
    }

    if (authorizations.relatedParties) {
      tabs.push(EntityTabs.RelatedParties);
    }

    if (authorizations.shares && shareClassesLength > 0) {
      tabs.push(EntityTabs.ShareRegister);
    }

    if (authorizations.invoices) {
      tabs.push(EntityTabs.Invoices);
    }

    const holdingsChildTabs: ParentTab[] = [];

    if (authorizations.banking) {
      holdingsChildTabs.push(EntityTabs.Banking);
    }

    if (authorizations.investments) {
      holdingsChildTabs.push(EntityTabs.Investments);
    }

    if (authorizations.assets) {
      holdingsChildTabs.push(EntityTabs.Assets);
    }

    if (authorizations.loans) {
      holdingsChildTabs.push(EntityTabs.Loans);
    }

    if (holdingsChildTabs.length > 0) {
      const holdingsTab: ParentTab = {
        ...EntityTabs.Holdings,
        children: holdingsChildTabs
      };

      tabs.push(holdingsTab);
    }

    if (authorizations.documents) {
      tabs.push(EntityTabs.Documents);
    }

    const appParams = AppParameterMap.from(this._route.snapshot.paramMap);

    return new TabList(tabs, appParams);
  }

}
